import { Loading } from "../app/Status";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useBuildingContext } from "../lib/buildingContext";
import { useClientContext } from "../lib/clientContext";
import { useMemo, useState, type FormEvent } from "react";
import {
  BUDGET_STATUS_LABELS,
  BUDGET_STATUSES,
  budgetTable,
  budgetTableByNlsfb,
  formatEuro,
  mainGroupLabel,
  matchesNlsfbFilter,
  nlsfbCategory,
  nlsfbCategoryLabel,
  nlsfbFilterLabel,
  nlsfbMainCode,
  MJOP_HORIZONS,
  reserveByBuilding,
  reserveSummaryText,
  todayIso,
  totalsByYear,
  WORK_TYPE_LABELS,
  WORK_TYPES,
  type BudgetItemDto,
  type BudgetStatus,
  type MjopResponse,
  type MjopSettingsResponse,
  type WorkType,
} from "@klantpunt/shared";
import { IconDownload, IconFile, IconSettings } from "../app/icons";
import { Modal } from "../app/Modal";
import { BudgetChart } from "../features/mjop/BudgetChart";
import { euroInput } from "../features/mjop/MjopFields";
import { PlanTable } from "../features/mjop/planning/PlanTable";
import { ReservePanel } from "../features/mjop/ReservePanel";
import { api, ApiError } from "../lib/api";
import { can, hasModule, useMe } from "../lib/auth";
import { useToast } from "../lib/toast";
import { parseEuro } from "./Platform";
import { Segmented } from "../app/Choice";
import { PageHeader } from "../app/PageHeader";

/**
 * MJOP-dashboard (docs/ASSETS-MJOP.md, "Schermen"): begroting per jaar over de horizon, gestapeld
 * per soort werk; kerncijfers, filters, tabel per NL-SfB (hoofdgroep met daaronder 3 posities) of per
 * gebouw, postenlijst per gebouw en
 * de instellingen (index met CBS-voorstel, horizon). Alles exclusief btw.
 */
const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");
const thisYear = () => Number(todayIso().slice(0, 4));

export function MjopPage() {
  const { data: me } = useMe();
  if (!hasModule(me, "mjop"))
    return (
      <div className="center">
        <p>Geen toegang tot de module MJOP.</p>
      </div>
    );
  return <Mjop canEdit={hasModule(me, "mjop", "edit")} />;
}

function Mjop({ canEdit }: { canEdit: boolean }) {
  // Volgt de gebouwkiezer in de bovenbalk; "Alle gebouwen" is hier "".
  const ctx = useBuildingContext();
  const building = ctx.id ?? "";
  const setBuilding = (v: string) => ctx.setBuilding(v || null);
  const [types, setTypes] = useState<WorkType[]>([...WORK_TYPES]);
  const [group, setGroup] = useState("");
  const [rowsBy, setRowsBy] = useState<"main" | "building">("main");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const { data: me } = useMe();
  const toast = useToast();
  // Met een gekozen klant (klantwisselaar) alleen de gebouwen van die klant.
  const { id: client } = useClientContext();
  const scope: Record<string, string> = building ? { building } : client ? { client } : {};
  const q = useQuery({
    queryKey: ["mjop", building, client],
    queryFn: () => api<MjopResponse>(`/mjop?${new URLSearchParams(scope).toString()}`),
  });
  const d = q.data;
  const rows = useMemo(
    () => (d?.rows ?? []).filter((r) => types.includes(r.workType) && matchesNlsfbFilter(r.nlsfbCode, group)),
    [d, types, group],
  );
  const years = d ? totalsByYear(rows, d.fromYear, d.toYear) : [];
  const total = years.reduce((s, y) => s + y.total, 0);
  const peak = years.reduce((m, y) => (y.total > (m?.total ?? -1) ? y : m), years[0]);
  // Kostendekkende reservering over de planhorizon én de kosten erna, tot de verste eerstvolgende vervanging.
  const reserveRows = useMemo(
    () => (d?.reserveRows ?? []).filter((r) => types.includes(r.workType) && matchesNlsfbFilter(r.nlsfbCode, group)),
    [d, types, group],
  );
  // Vervangingen na de planhorizon: niet in de grafiek en de tabel, wel in de reservering.
  const later = useMemo(() => {
    const repl = reserveRows.filter((r) => r.workType === "replacement");
    if (!repl.length) return null;
    const years = repl.map((r) => r.year);
    return {
      count: repl.length,
      cents: repl.reduce((s, r) => s + r.amountCents, 0),
      from: Math.min(...years),
      to: Math.max(...years),
    };
  }, [reserveRows]);
  // De reservering is het hele fonds per gebouw: alle kosten, los van de filters.
  const reserve = useMemo(
    () =>
      d
        ? reserveByBuilding(
            [...d.rows, ...d.reserveRows],
            d.reservePlans,
            d.buildings.map((b) => b.id),
            d.settings.indexPercent,
            d.fromYear,
            d.reserveToYear,
          )
        : null,
    [d],
  );
  // Filterkeuzes: per hoofdgroep de categorieën op 3 posities die in de begroting voorkomen.
  const filterGroups = useMemo(() => {
    const cats = [...new Set((d?.rows ?? []).map((r) => nlsfbCategory(r.nlsfbCode)))];
    const mains = [...new Set(cats.filter((c): c is string => !!c).map(nlsfbMainCode))].sort(
      (a, b) => (a === "0" ? 10 : Number(a)) - (b === "0" ? 10 : Number(b)),
    );
    return {
      mains: mains.map((m) => ({
        main: m,
        cats: cats
          .filter((c): c is string => !!c && nlsfbMainCode(c) === m)
          .sort((a, b) => a.localeCompare(b, "nl", { numeric: true })),
      })),
      none: cats.includes(null),
    };
  }, [d]);

  // Tabel: jaren als kolommen; NL-SfB (hoofdgroep met subtotaal, daaronder 3 posities) of gebouwen als rijen.
  const table = useMemo(() => {
    if (!d) return [];
    if (rowsBy === "main") return budgetTableByNlsfb(rows, d.fromYear, d.toYear);
    return budgetTable(
      rows,
      d.fromYear,
      d.toYear,
      (r) => d.buildings.find((b) => b.id === r.buildingId)?.code ?? "?",
    ).map((r) => ({ ...r, children: [] as { key: string; amounts: number[]; total: number }[] }));
  }, [d, rows, rowsBy]);

  const exportQuery = new URLSearchParams({
    ...scope,
    ...(types.length < WORK_TYPES.length ? { types: types.join(",") } : {}),
    ...(group ? { group } : {}),
  }).toString();

  async function makePdf() {
    if (!d) return;
    setPdfBusy(true);
    try {
      const [{ buildMjopReport, mjopFilename }, web] = await Promise.all([
        import("../features/report/buildMjop"),
        import("../features/report/browser"),
      ]);
      const one = building ? d.buildings.find((b) => b.id === building) : undefined;
      const filter = [
        types.length < WORK_TYPES.length ? types.map((t) => WORK_TYPE_LABELS[t]).join(", ") : null,
        nlsfbFilterLabel(group),
      ].filter(Boolean);
      const date = new Date();
      const res = await buildMjopReport({
        data: { ...d, rows, reserveRows },
        ...(reserve ? { reserve } : {}),
        scope: one ? { code: one.code, name: one.name } : { code: null, name: me?.org?.name ?? "Alle gebouwen" },
        shown: types,
        filter: filter.length ? filter.join(" · ") : null,
        author: me?.user.name ?? "",
        date,
        assets: await web.loadAssets(me?.org),
      });
      web.download(res.bytes, mjopFilename(one?.code ?? null, d.fromYear, d.toYear));
      toast(`MJOP als PDF gemaakt (${res.pages} pagina's)`);
    } catch {
      toast("De PDF kon niet worden gemaakt. Probeer het opnieuw.");
    } finally {
      setPdfBusy(false);
    }
  }

  return (
    <div className="page mjop">
      <PageHeader
        label="Begroting"
        title="MJOP"
        menu={
          <>
            <button type="button" className="menuitem" disabled={!d || pdfBusy} onClick={() => void makePdf()}>
              <IconFile size={20} aria-hidden="true" />
              {pdfBusy ? "PDF maken…" : "PDF"}
            </button>
            <a className="menuitem" href={`/api/mjop/export.xlsx?${exportQuery}`} download>
              <IconDownload size={20} aria-hidden="true" />
              Excel
            </a>
            <button type="button" className="menuitem" onClick={() => setSettingsOpen(true)}>
              <IconSettings size={20} aria-hidden="true" />
              Instellingen
            </button>
          </>
        }
      />
      {d && (
        <p className="muted small">
          {d.fromYear}–{d.toYear} · index {d.settings.indexPercent.toLocaleString("nl-NL")}% per jaar vanaf prijspeil{" "}
          {d.settings.baseYear} · alle bedragen exclusief btw
        </p>
      )}

      <div className="mjop-filters" role="group" aria-label="Filters">
        <label className="field">
          <span className="label">Gebouw</span>
          <select value={building} onChange={(e) => setBuilding(e.target.value)}>
            <option value="">Alle gebouwen</option>
            {d?.buildings.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} · {b.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">NL-SfB</span>
          <select value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="">Alle elementen</option>
            {filterGroups.mains.map(({ main, cats }) => (
              <optgroup key={main} label={mainGroupLabel(main)}>
                <option value={main}>{mainGroupLabel(main)} (alles)</option>
                {cats.map((c) => (
                  <option key={c} value={c}>
                    {nlsfbCategoryLabel(c)}
                  </option>
                ))}
              </optgroup>
            ))}
            {filterGroups.none && <option value="none">Niet ingedeeld</option>}
          </select>
        </label>
        <fieldset className="mjop-types">
          <legend className="label">Soort werk</legend>
          {WORK_TYPES.map((w) => (
            <label key={w} className="chip toggle">
              <input
                type="checkbox"
                checked={types.includes(w)}
                onChange={(e) => setTypes(e.target.checked ? [...types, w] : types.filter((x) => x !== w))}
              />
              <i className={`wt wt-${w}`} aria-hidden="true" />
              {WORK_TYPE_LABELS[w]}
            </label>
          ))}
        </fieldset>
      </div>

      <div className="pf-kpis" role="group" aria-label="Kerncijfers">
        <span className="pf-kpi">
          <span className="label">Totaal {d ? `${d.fromYear}–${d.toYear}` : ""}</span>
          <strong className="num" data-testid="mjop-total">
            {formatEuro(total)}
          </strong>
        </span>
        <span className="pf-kpi">
          <span className="label">Reservering per jaar</span>
          <strong className="num" data-testid="mjop-reserve">
            {formatEuro(reserve?.perYearCents ?? 0)}
          </strong>
          <span className="muted small">
            {reserve && d && reserve.perYearCents > 0
              ? reserveSummaryText(reserve, d.fromYear, d.reserveToYear)
              : "niets te reserveren"}
          </span>
        </span>
        <span className="pf-kpi">
          <span className="label">Duurste jaar</span>
          <strong className="num">{peak && peak.total > 0 ? `${peak.year} · ${formatEuro(peak.total)}` : "—"}</strong>
        </span>
      </div>

      <section className="panel" aria-busy={q.isPending} aria-label="Begroting per jaar">
        {q.isPending ? <Loading /> : <BudgetChart years={years} shown={types} />}
        {d && later && (
          <p className="hint" data-testid="mjop-after-horizon">
            Na {d.toYear} {later.count === 1 ? "staat nog 1 vervanging" : `staan nog ${later.count} vervangingen`}{" "}
            gepland ({later.from === later.to ? later.from : `${later.from}–${later.to}`}, samen{" "}
            {formatEuro(later.cents)} met index). {later.count === 1 ? "Die valt" : "Die vallen"} buiten de planhorizon
            van {d.settings.horizonYears} jaar en {later.count === 1 ? "staat" : "staan"} daarom niet in de grafiek en
            de tabel, maar {later.count === 1 ? "telt" : "tellen"} wel mee in de reservering.{" "}
            {canEdit && (
              <button type="button" className="linkbtn inline" onClick={() => setSettingsOpen(true)}>
                Planhorizon aanpassen
              </button>
            )}
          </p>
        )}
      </section>

      {d && reserve && (
        <ReservePanel d={d} reserve={reserve} building={building} canEdit={canEdit} onPickBuilding={setBuilding} />
      )}

      <section className="panel" aria-label="Meerjarentabel">
        <div className="titlerow">
          <h2>Per jaar</h2>
          <Segmented
            label="Rijen"
            items={[
              { value: "main", label: "Per NL-SfB" },
              { value: "building", label: "Per gebouw" },
            ]}
            value={rowsBy}
            onChange={setRowsBy}
          />
        </div>
        {!table.length ? (
          <p className="muted">
            Nog niets begroot. Vul bij assets levensduur en vervangingsprijs in, geef bevindingen een raming, of voeg
            posten toe per gebouw.
          </p>
        ) : (
          <div className="tscroll">
            <table className="it mjop-table">
              <thead>
                <tr>
                  <th>{rowsBy === "main" ? "NL-SfB" : "Gebouw"}</th>
                  {years.map((y) => (
                    <th key={y.year} className="num">
                      {y.year}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.flatMap(({ key: k, amounts: vals, children }) => [
                  <tr key={k} className={rowsBy === "main" ? "grp" : undefined}>
                    <th scope="row">{k}</th>
                    {vals.map((v, i) => (
                      <td key={i} className="num">
                        {v ? formatEuro(v) : "—"}
                      </td>
                    ))}
                  </tr>,
                  ...children.map((c) => (
                    <tr key={`${k}/${c.key}`} className="sub">
                      <th scope="row">{c.key}</th>
                      {c.amounts.map((v, i) => (
                        <td key={i} className="num">
                          {v ? formatEuro(v) : "—"}
                        </td>
                      ))}
                    </tr>
                  )),
                ])}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">Totaal</th>
                  {years.map((y) => (
                    <td key={y.year} className="num">
                      <strong>{y.total ? formatEuro(y.total) : "—"}</strong>
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      {building ? (
        <BuildingPlan buildingId={building} canEdit={canEdit} />
      ) : (
        <p className="muted small">Kies een gebouw om de uitvoeringen te zien en te bewerken.</p>
      )}

      {settingsOpen && <SettingsDialog canEdit={canEdit} onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}

// ---------- uitvoeringen per gebouw ----------

function BuildingPlan({ buildingId, canEdit }: { buildingId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: me } = useMe();
  // Posten voor *Meer velden* (hoeveelheid, prijspeil, status).
  const q = useQuery({
    queryKey: ["budget-items", buildingId],
    queryFn: () => api<BudgetItemDto[]>(`/buildings/${buildingId}/budget-items`),
    enabled: canEdit,
  });
  const [editing, setEditing] = useState<string | null>(null);
  const editItem = q.data?.find((i) => i.id === editing) ?? null;
  return (
    <>
      <PlanTable
        buildingId={buildingId}
        canEditItems={canEdit}
        canEditObligations={can(me, "asset.manage")}
        onDetails={setEditing}
      />
      {editItem && (
        <ItemDialog
          buildingId={buildingId}
          item={editItem}
          onClose={() => setEditing(null)}
          onSaved={(l) => {
            qc.setQueryData(["budget-items", buildingId], l);
            void qc.invalidateQueries({ queryKey: ["mjop"] });
            setEditing(null);
            toast("Post opgeslagen");
          }}
        />
      )}
    </>
  );
}

function ItemDialog({
  buildingId,
  item,
  onClose,
  onSaved,
}: {
  buildingId: string;
  item: BudgetItemDto | null;
  onClose: () => void;
  onSaved: (l: BudgetItemDto[]) => void;
}) {
  const toast = useToast();
  // Bij een asset of bevinding ligt de soort vast (vervanging of herstel).
  const fixedType = Boolean(item?.assetId || item?.findingId);
  const [workType, setWorkType] = useState<WorkType>(item?.workType ?? "maintenance");
  const [description, setDescription] = useState(item?.description ?? "");
  const [price, setPrice] = useState(euroInput(item?.unitPriceCents));
  const [qty, setQty] = useState(String(item?.quantity ?? 1).replace(".", ","));
  const [first, setFirst] = useState(String(item?.firstYear ?? thisYear() + 1));
  const [cycle, setCycle] = useState(item?.cycleYears?.toString() ?? "");
  const [last, setLast] = useState(item?.lastYear?.toString() ?? "");
  const [level, setLevel] = useState(item?.priceLevelYear?.toString() ?? String(thisYear()));
  const [status, setStatus] = useState<BudgetStatus>(item?.status ?? "planned");
  const [busy, setBusy] = useState(false);
  const int = (v: string) => (v.trim() ? Number(v.trim()) : null);
  const bad = parseEuro(price) === null || !/^\d{4}$/.test(first.trim());
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (bad) return;
    const body = {
      ...(fixedType ? {} : { workType }),
      description,
      unitPriceCents: parseEuro(price),
      quantity: Number(qty.replace(",", ".")) || 1,
      firstYear: Number(first),
      cycleYears: int(cycle),
      lastYear: int(last),
      priceLevelYear: int(level),
    };
    setBusy(true);
    try {
      onSaved(
        item
          ? await api<BudgetItemDto[]>(`/budget-items/${item.id}`, { method: "PATCH", body: { ...body, status } })
          : await api<BudgetItemDto[]>(`/buildings/${buildingId}/budget-items`, {
              method: "POST",
              body: { id: crypto.randomUUID(), ...body },
            }),
      );
    } catch (err) {
      toast(errMsg(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title={item ? "Post bewerken" : "Nieuwe post"}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="submit" form="budgetform" className="btn primary" disabled={busy || bad || !description.trim()}>
            Opslaan
          </button>
        </>
      }
    >
      <form id="budgetform" className="stack" onSubmit={submit}>
        <label className="field">
          <span className="label">Omschrijving *</span>
          <input
            required
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="bijv. Gevel schilderen"
          />
        </label>
        <div className="formgrid">
          <label className="field">
            <span className="label">Soort werk</span>
            <select value={workType} disabled={fixedType} onChange={(e) => setWorkType(e.target.value as WorkType)}>
              {WORK_TYPES.filter((w) => fixedType || w !== "repair").map((w) => (
                <option key={w} value={w}>
                  {WORK_TYPE_LABELS[w]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Prijs per eenheid (€) *</span>
            <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Hoeveelheid</span>
            <input inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Prijspeil (jaar)</span>
            <input inputMode="numeric" value={level} onChange={(e) => setLevel(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Eerste jaar *</span>
            <input inputMode="numeric" value={first} onChange={(e) => setFirst(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Elke … jaar</span>
            <input
              inputMode="numeric"
              value={cycle}
              placeholder="leeg = eenmalig"
              onChange={(e) => setCycle(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="label">Laatste jaar</span>
            <input inputMode="numeric" value={last} placeholder="optioneel" onChange={(e) => setLast(e.target.value)} />
          </label>
          {item && (
            <label className="field">
              <span className="label">Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value as BudgetStatus)}>
                {BUDGET_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {BUDGET_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </form>
    </Modal>
  );
}

// ---------- instellingen ----------

function SettingsDialog({ canEdit, onClose }: { canEdit: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ["mjop-settings"], queryFn: () => api<MjopSettingsResponse>("/org/mjop-settings") });
  return (
    <Modal title="Instellingen MJOP" onClose={onClose}>
      {!q.data ? (
        <Loading />
      ) : (
        <SettingsForm
          s={q.data}
          canEdit={canEdit}
          onSaved={(r) => {
            qc.setQueryData(["mjop-settings"], r);
            void qc.invalidateQueries({ queryKey: ["mjop"] });
            toast("Instellingen opgeslagen");
            onClose();
          }}
        />
      )}
    </Modal>
  );
}

function SettingsForm({
  s,
  canEdit,
  onSaved,
}: {
  s: MjopSettingsResponse;
  canEdit: boolean;
  onSaved: (r: MjopSettingsResponse) => void;
}) {
  const toast = useToast();
  const [index, setIndex] = useState(String(s.indexPercent).replace(".", ","));
  const [base, setBase] = useState(s.baseYearSetting?.toString() ?? "");
  const [horizon, setHorizon] = useState(s.horizonYears);
  const save = useMutation({
    mutationFn: () =>
      api<MjopSettingsResponse>("/org/mjop-settings", {
        method: "PUT",
        body: {
          indexPercent: Number(index.replace(",", ".")),
          baseYear: base.trim() ? Number(base) : null,
          horizonYears: horizon,
        },
      }),
    onSuccess: onSaved,
    onError: (e) => toast(errMsg(e)),
  });
  const sg = s.suggestion;
  const pct = (n: number) => `${n.toLocaleString("nl-NL")}%`;
  const bad = !/^-?\d+([.,]\d+)?$/.test(index.trim());
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (!bad) save.mutate();
      }}
    >
      <label className="field">
        <span className="label">Indexpercentage per jaar</span>
        <input
          inputMode="decimal"
          value={index}
          disabled={!canEdit}
          aria-invalid={bad}
          onChange={(e) => setIndex(e.target.value)}
        />
      </label>
      <div className="obcard sugg" role="note" aria-label="Voorstel CBS">
        {sg.fallback ? (
          <p className="small">
            Voorstel: <strong>{pct(sg.lastYearPercent ?? 3)}</strong> · geen actuele CBS-cijfers
          </p>
        ) : (
          <p className="small">
            Voorstel op basis van CBS, peildatum {sg.period}: jaarmutatie{" "}
            <strong>{pct(sg.lastYearPercent ?? 0)}</strong>
            {sg.fiveYearPercent != null && (
              <>
                , gemiddeld over 5 jaar <strong>{pct(sg.fiveYearPercent)}</strong>
              </>
            )}
            .
          </p>
        )}
        <p className="hint">
          Outputprijsindex bouwkosten nieuwbouwwoningen (CBS 85772NED). CBS meet terug en voorspelt niet: dit is zoals
          het ging, geen verwachting. Het voorstel wordt nooit vanzelf overgenomen.
        </p>
        {canEdit && (
          <div className="btnrow">
            {sg.lastYearPercent != null && (
              <button
                type="button"
                className="btn small"
                onClick={() => setIndex(String(sg.lastYearPercent).replace(".", ","))}
              >
                {pct(sg.lastYearPercent)} overnemen
              </button>
            )}
            {sg.fiveYearPercent != null && (
              <button
                type="button"
                className="btn small"
                onClick={() => setIndex(String(sg.fiveYearPercent).replace(".", ","))}
              >
                {pct(sg.fiveYearPercent)} overnemen
              </button>
            )}
          </div>
        )}
      </div>
      <div className="formgrid">
        <label className="field">
          <span className="label">Basisjaar (prijspeil)</span>
          <input
            inputMode="numeric"
            value={base}
            disabled={!canEdit}
            placeholder={`leeg = ${thisYear()}`}
            onChange={(e) => setBase(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="label">Planhorizon</span>
          <select value={horizon} disabled={!canEdit} onChange={(e) => setHorizon(Number(e.target.value))}>
            {MJOP_HORIZONS.map((h) => (
              <option key={h} value={h}>
                {h} jaar
              </option>
            ))}
          </select>
        </label>
      </div>
      {canEdit && (
        <div>
          <button type="submit" className="btn primary" disabled={save.isPending || bad}>
            Instellingen opslaan
          </button>
        </div>
      )}
    </form>
  );
}
